import { useEffect, useState } from 'react';
import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';

import { fetchVersionFileContent } from '../../utils/fetch-version-file-content';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import {
  diffActionFiles,
  isActionFile,
  type ActionFileChange,
} from './action-file-diff';

export type ActionDiffState =
  | { status: 'loading'; changes: null; error: null }
  | { status: 'ready'; changes: ActionFileChange[]; error: null }
  | { status: 'error'; changes: null; error: string };

/** Published versions don't change: their action files are read once a session. */
const publishedCache = new Map<string, Promise<Record<string, string>>>();

const readActionFiles = async (
  projectId: string,
  versionId: string
): Promise<Record<string, string>> => {
  const res = await urqlGql.getVersionById({ versionId, projectId });
  if (res.error) throw new Error(res.error.message);
  const files: string[] = res.data?.getVersionById?.data?.files ?? [];
  const actionPaths = files.filter(isActionFile);
  const entries = await Promise.all(
    actionPaths.map(
      async (filePath) =>
        [
          filePath,
          await fetchVersionFileContent({ projectId, versionId, filePath }),
        ] as const
    )
  );
  return Object.fromEntries(entries);
};

/**
 * The action code a publish changes: the draft's action scripts against the
 * version it replaces (`baselineVersion`), or everything on a first publish.
 * Re-read whenever Deploy opens or the draft's file list changes.
 */
export const useActionFileDiff = (
  baselineVersion: string | null,
  /** A published version to compare instead of the draft (promoting it). */
  targetVersion: string | null = null
): ActionDiffState => {
  const { projectId, customerId, botName, draftVersion, draftFiles } =
    useProStudioCtxSelector(
      (ctx) => ({
        projectId: ctx.project?.id,
        customerId: ctx.customerId,
        botName: ctx.botName,
        draftVersion: ctx.draftVersion,
        draftFiles: (ctx.draft?.files ?? []).join('|'),
      }),
      { isEqualityDeep: true }
    );
  const [state, setState] = useState<ActionDiffState>({
    status: 'loading',
    changes: null,
    error: null,
  });

  useEffect(() => {
    if (!projectId || !customerId || !botName || !draftVersion)
      return undefined;
    let cancelled = false;
    setState({ status: 'loading', changes: null, error: null });
    const id = (v: string) => `${customerId}.${botName}.${v}`;

    // A published version's files never change, so they're read once.
    const published = (version: string) => {
      const key = `${projectId}:${id(version)}`;
      let read = publishedCache.get(key);
      if (!read) {
        read = readActionFiles(projectId, id(version));
        publishedCache.set(key, read);
        read.catch(() => publishedCache.delete(key));
      }
      return read;
    };
    const before = baselineVersion ? published(baselineVersion) : null;
    const after = targetVersion
      ? published(targetVersion)
      : readActionFiles(projectId, id(draftVersion));

    Promise.all([before, after])
      .then(([was, now]) => {
        if (!cancelled) {
          setState({
            status: 'ready',
            changes: diffActionFiles(was, now),
            error: null,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            status: 'error',
            changes: null,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    customerId,
    botName,
    draftVersion,
    baselineVersion,
    targetVersion,
    draftFiles,
  ]);

  return state;
};
